import {
    BaseChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/base-chart-config-helper';
import { CHART_TYPE } from '@tencent/chart/lib/chart-ui/interface';
import {
    DataLabel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/data-label';
import {
    DoubleValueAxis,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/axis';
import type { IBubbleChartDataItem } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-item/data-item.interface';
import type { IChartRendererConfig } from '@tencent/chart/lib/chart-ui/interface';
import type {
    IBubbleChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/bubble-chart.interface';
import type {
    ChartUIDataItem,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';

export interface BubbleChartUIDataItem extends ChartUIDataItem {
    // 实际绘制气泡的半径
    size: number;
    // 数据中的bubbleSize字段
    originSize: number;
}

export class BubbleChartConfigHelper extends BaseChartConfigHelper {
    public get type() {
        return CHART_TYPE.BUBBLE_CHART;
    }

    public getRenderConfig(): IChartRendererConfig {
        const conf = super.getRenderConfig();

        // 如果不显示负值气泡，就过滤
        if (!(this.subChart as IBubbleChartProperty).showNegBubbles) {
            conf.data = conf.data
                .filter(item => (item as BubbleChartUIDataItem).originSize >= 0);
        }

        return conf;
    }

    protected getGeometrySize = (chartDataItem: BubbleChartUIDataItem) =>
        // 1. 原size，根据面积 / 宽度，算出size（半径）
        // 2. 根据缩放，计算size
        chartDataItem.size;

    protected normalizeDataItem(dataItem: IBubbleChartDataItem, dataItemIndex: number): BubbleChartUIDataItem {
        const chartItemData = super.normalizeDataItem(dataItem, dataItemIndex);

        if (dataItem.originSize < 0) {
            // 负值气泡的填充是透明的，但是不体现在ooxml里面
            chartItemData.fill = 'transparent';
            // 负值气泡的边框默认黑色
            chartItemData.border = {
                ...chartItemData.border,
                stroke: !chartItemData.border?.stroke ? '#000' : chartItemData.border?.stroke,
                lineWidth: !chartItemData.border?.lineWidth ? 1.5 : chartItemData.border?.lineWidth,
            };

            chartItemData.background = {
                noFill: 1,
            };
            const hasStroke = chartItemData.outline?.color
                || chartItemData.outline?.gradFill
                || chartItemData.outline?.noFill;
            chartItemData.outline = {
                ...chartItemData.outline,
                ...(!hasStroke ? { color: '#000000' } : {}),
                width: chartItemData.outline?.width ?? 0.75,
            };
        }

        return {
            ...chartItemData,
            size: dataItem.size,
            originSize: dataItem.originSize,
        };
    }

    protected normalizeData() {
        super.normalizeData();
        this.data = this.data.filter(item => !this.isInvalidDataItem(item));
    }

    protected getDataLabel(label: IDataLabelProperty): DataLabel {
        return new BubbleDataLabel(label, this.context);
    }

    protected getAxis() {
        return new DoubleValueAxis(this.subChart, this.context.getChartModel()).init();
    }

    private isInvalidDataItem(item) {
        return this.isValueNil(item.originCategory) || this.isValueNil(item.originValue);
    }

    // 这里有区别于isEmptyValue的判定。在气泡图中，即使是字符串类型的category或者value(非空值)，bubbleSize也会参与计算，同时坐标也会降级为索引
    private isValueNil(value) {
        return value === '' || value === undefined || value === null;
    }
}

class BubbleDataLabel extends DataLabel {}
